import React from 'react';
import classNames from 'classnames';
import type { ColorId } from 'unsplash-js';
import Button from './Button';
import * as styles from './UnsplashColorPicker.module.scss';
import SVGIcon from './SVGIcon';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';

export type SupportedColorId = Exclude<ColorId, 'teal' | 'black_and_white'>;

type RGBColor = [number, number, number];

const COLORS: Record<SupportedColorId, RGBColor> = {
  black: [0, 0, 0],
  white: [255, 255, 255],
  yellow: [252, 229, 21],
  red: [221, 9, 9],
  purple: [115, 44, 197],
  magenta: [255, 0, 255],
  green: [55, 100, 45],
  blue: [79, 164, 234],
  orange: [234, 115, 46],
};

const LUMA_THRESHOLD = 0.5;

// Luma coefficients based on https://en.wikipedia.org/wiki/Rec._709
const LUMA_COEFFICIENTS: [number, number, number] = [0.2126, 0.7152, 0.0722];

function calculateLuma([r, g, b]: RGBColor) {
  return (
    (LUMA_COEFFICIENTS[0] * r +
      LUMA_COEFFICIENTS[1] * g +
      LUMA_COEFFICIENTS[2] * b) /
    255
  );
}

function rgbColorToString(color: RGBColor, alpha?: number) {
  if (alpha != null) {
    return `rgba(${color.join(',')},${alpha})`;
  }
  return `rgb(${color.join(',')})`;
}

interface UnsplashColorProps {
  color?: SupportedColorId | null;
  selected?: boolean;
  textColor?: 'black' | 'white';
}

export const UnsplashColor = ({
  color,
  selected,
  textColor,
}: UnsplashColorProps) => {
  return (
    <div
      className={classNames(styles.color, {
        [styles.defaultColor]: color == null,
      })}
      style={
        color
          ? {
              backgroundColor: rgbColorToString(COLORS[color]),
              color: textColor,
            }
          : undefined
      }
    >
      {selected && <SVGIcon>{checkIcon}</SVGIcon>}
    </div>
  );
};

interface UnsplashColorsProps {
  value: SupportedColorId | null;
  onSelect(id: SupportedColorId | null): void;
}

const UnsplashColors = ({ onSelect, value }: UnsplashColorsProps) => {
  return (
    <div className={styles.colors}>
      <span
        style={
          {
            '--ring-color': rgbColorToString(COLORS.white, 0.2),
          } as React.CSSProperties
        }
      >
        <Button
          className={classNames(styles.colorButton, styles.defaultColor, {
            [styles.isSelected]: value == null,
          })}
          onClick={() => onSelect(null)}
        >
          <UnsplashColor />
        </Button>
      </span>
      {Object.entries(COLORS).map(([key, color]) => {
        const colorId = key as SupportedColorId;
        const isSelected = value === colorId;
        return (
          <span
            key={colorId}
            style={
              {
                '--ring-color': rgbColorToString(color, 0.2),
              } as React.CSSProperties
            }
          >
            <Button
              className={classNames(styles.colorButton, styles[colorId], {
                [styles.isSelected]: isSelected,
              })}
              onClick={() => onSelect(colorId)}
            >
              <UnsplashColor
                color={colorId}
                selected={isSelected}
                textColor={
                  calculateLuma(color) <= LUMA_THRESHOLD ? 'white' : 'black'
                }
              />
            </Button>
          </span>
        );
      })}
    </div>
  );
};

export default UnsplashColors;
